import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import Alert from '../../../../_shared/components/Alert';
import CopyToClipboard from '../../../../_shared/components/CopyToClipboard';
import VehicleInfoModal from './VehicleInfoModal';
import MixpanelConfig from '../../../../_shared/constants/mixpanelConfig';
import {
  TradeContext,
  TradeContextName,
  TradeStateName
} from '../../../../_shared/types/trade';
import ROUTES from '../../../Routes/constants';
import Detail from '../../../../_shared/components/Detail';
import Loading from '../../../../_shared/components/Loading';
import copyToClipBoard from '../../../../_shared/utils/copyToClipBoard';
import Pill from '../../../../_shared/components/Pill';
import { BidSource } from '../../../../_shared/types/auction';
import useAnalytics from '../../../../_shared/hooks/useAnalytics';

import {
  AsIs,
  AsIsContainer,
  Make,
  Model,
  StyledBox,
  StyledDetail,
  StyledModalButton,
  StyledVehicleInfo,
  Trim,
  TrimVin,
  Vin,
  VinText,
  Year
} from './Styles';
import TRToast from '../../../../_shared/helpers/TRToast';

const VehicleInfo = ({
  tradeDetails,
  tradeId,
  tradeContext,
  tradeState,
  isOMP
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const isSelling = tradeContext === TradeContext.Sell;

  const track = useAnalytics();

  const location = window.location.href.includes(ROUTES.BUYING)
    ? BidSource.MINI_VDP
    : BidSource.VDP_OVERLAY;

  const copyToClipBoardHandler = source => {
    copyToClipBoard(tradeDetails?.vin);

    TRToast(
      <Alert
        variant="information"
        messages={[<FormattedMessage id="vdp.success.vinCopied" />]}
      />
    );

    track(MixpanelConfig.names.copyVinToClipBoard, {
      source: location === BidSource.MINI_VDP ? `mini ${source}` : source,
      trade_id: tradeId,
      auction_id: tradeDetails?.auctionSummary?.id,
      trade_state: TradeStateName[tradeState]
    });
  };

  const openModal = useCallback(() => {
    track(MixpanelConfig.names.viewFullSpecsFromVDP, {
      trade_id: tradeDetails?.id,
      auction_id: tradeDetails?.auctionSummary?.id,
      year: tradeDetails?.year,
      make: tradeDetails?.manufacturerName,
      model: tradeDetails?.modelName,
      vin: tradeDetails?.vin,
      trim: tradeDetails?.trim,
      asIs: tradeDetails?.isAsIs,
      trade_state: TradeStateName[tradeState],
      trade_state_section: TradeContextName[tradeContext]
    });
    setIsOpen(true);
  }, [track, tradeContext, tradeState, tradeDetails]);

  const closeModal = () => {
    setIsOpen(false);
  };

  const truncateVin = vin => {
    if (!vin) {
      return '';
    }

    const truncatedVin = vin.slice(-6);
    return `...${truncatedVin}`;
  };

  const lineBreakTrim = () => {
    return isSelling && !isOMP ? (
      <Trim>{tradeDetails.trim}</Trim>
    ) : (
      <span>{`${tradeDetails.trim} | `}</span>
    );
  };

  return (
    <StyledVehicleInfo isSelling={isSelling} data-testid="vdp-vehicleinfo">
      <StyledBox id="vehicleTitleDetails">
        {tradeDetails?.isLoading ? (
          <Loading height="51px" />
        ) : (
          <>
            {tradeDetails.isAsIs && (
              <AsIsContainer className="vdp-asis">
                <AsIs testId="vdp-overview-badge-asis">
                  {/* As-is */}
                  <FormattedMessage id="vdp.asis" />
                </AsIs>
              </AsIsContainer>
            )}
            <Year>{tradeDetails.year}</Year>
            <Make className="notranslate">{tradeDetails.manufacturerName}</Make>
            <Model className="notranslate">{tradeDetails.modelName}</Model>
          </>
        )}
      </StyledBox>
      <StyledBox>
        {tradeDetails?.isLoading ? (
          <Loading height="25px" />
        ) : (
          <>
            <TrimVin displayInherit={!isSelling}>
              {lineBreakTrim()}
              <CopyToClipboard
                onClick={() => copyToClipBoardHandler('VDP-Overview')}
                testId="vinCopyButton"
              >
                <VinText id="vinCopyButtonText">
                  <FormattedMessage id="vdp.overview.copyVin" />
                </VinText>
                <Vin>
                  <Vin.Web id="vinCopyWeb">{tradeDetails?.vin}</Vin.Web>
                  <Vin.Mobile id="vinCopyMobile">
                    {truncateVin(tradeDetails?.vin)}
                  </Vin.Mobile>
                </Vin>
              </CopyToClipboard>
            </TrimVin>
          </>
        )}
      </StyledBox>
      <StyledBox id="vehicleTradeDetails">
        {tradeDetails?.isLoading ? (
          <Loading height="41px" />
        ) : (
          <>
            <StyledDetail>
              <Detail.Label>
                {/* Odometer */}
                <FormattedMessage id="tr5.odometer" />
              </Detail.Label>
              <Detail.Value>{tradeDetails.odometerDisplay}</Detail.Value>
            </StyledDetail>
            <StyledDetail>
              <Detail.Label>
                {/* Transmission & drivetrain */}
                <FormattedMessage id="vdp.overview.transmissionDriveTrain" />
              </Detail.Label>
              <Detail.Value>{`${tradeDetails.transmission}, ${tradeDetails.driveTrain}`}</Detail.Value>
              <Detail.Value>{`${tradeDetails.powerTrain}`}</Detail.Value>
            </StyledDetail>
            <StyledDetail>
              <Detail.Label>
                {/* Date expected */}
                <FormattedMessage id="vdp.overview.dateExpected" />
              </Detail.Label>
              {tradeDetails.hasDateExpectedExceededLimit ? (
                <Pill
                  testId="dateExpectedLimitWarning"
                  variant="warning"
                  message={[tradeDetails.dateAvailable]}
                />
              ) : (
                <Detail.Value>{tradeDetails.dateAvailable}</Detail.Value>
              )}
            </StyledDetail>
            <StyledDetail>
              <Detail.Label>
                {/* Full specs & features */}
                <FormattedMessage id="vdp.overview.specsFeatures" />
              </Detail.Label>
              <Detail.Value>
                <StyledModalButton onClick={openModal}>
                  <FormattedMessage id="vdp.overview.viewAll" />
                </StyledModalButton>
              </Detail.Value>
            </StyledDetail>
          </>
        )}
      </StyledBox>
      <VehicleInfoModal
        isOpen={isOpen}
        closeModal={closeModal}
        tradeDetails={tradeDetails}
        copyVin={copyToClipBoardHandler}
      />
    </StyledVehicleInfo>
  );
};

export default VehicleInfo;
